import { useCallback, useEffect, useRef, useState } from 'react';
import { getSyncStatus } from '../services/tenantService';
import socketService from '../services/socketService';

const normalizeSyncStatus = (response) => {
  if (!response) return null;
  if (response.success && response.data) return response.data;
  if (response.jobs || response.proposals || response.rooms) return response;
  return null;
};

const withJobsState = (state) => ({
  ...state,
  jobs: {
    ...(state?.jobs || {}),
  },
});

export function useJobSyncStatus(options = {}) {
  const {
    enabled = true,
    pollMs = 10000,
    onSyncStarted,
    onSyncCompleted,
    onSyncFailed,
  } = options;

  const [syncStatus, setSyncStatus] = useState(null);
  const callbacksRef = useRef({
    onSyncStarted,
    onSyncCompleted,
    onSyncFailed,
  });

  useEffect(() => {
    callbacksRef.current = {
      onSyncStarted,
      onSyncCompleted,
      onSyncFailed,
    };
  }, [onSyncStarted, onSyncCompleted, onSyncFailed]);

  const refreshSyncStatus = useCallback(async () => {
    if (!enabled) return;
    try {
      const response = await getSyncStatus();
      const normalized = normalizeSyncStatus(response);
      if (normalized) {
        setSyncStatus(normalized);
      }
    } catch {
      // Fail silently for UI continuity
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setSyncStatus(null);
      return undefined;
    }

    refreshSyncStatus();
    const pollInterval = setInterval(refreshSyncStatus, pollMs);

    const handleSyncStarted = (eventData) => {
      setSyncStatus((prev) => {
        const previous = withJobsState(prev || {});
        const next = {
          ...previous,
          jobs: {
            ...previous.jobs,
            isSyncing: true,
            lastError: null,
            syncStartTime: Date.now(),
          },
        };
        callbacksRef.current.onSyncStarted?.({
          eventData,
          previousJobs: previous.jobs,
          nextJobs: next.jobs,
        });
        return next;
      });
    };

    const handleSyncCompleted = (eventData) => {
      setSyncStatus((prev) => {
        const previous = withJobsState(prev || {});
        const next = {
          ...previous,
          jobs: {
            ...previous.jobs,
            isSyncing: false,
            lastSyncTime: new Date().toISOString(),
            lastSyncResult: eventData?.result || previous.jobs.lastSyncResult,
            lastError: null,
            syncStartTime: undefined,
          },
        };
        callbacksRef.current.onSyncCompleted?.({
          eventData,
          previousJobs: previous.jobs,
          nextJobs: next.jobs,
        });
        return next;
      });
    };

    const handleSyncFailed = (eventData) => {
      setSyncStatus((prev) => {
        const previous = withJobsState(prev || {});
        const next = {
          ...previous,
          jobs: {
            ...previous.jobs,
            isSyncing: false,
            lastError: eventData?.error || 'Sync failed',
            syncStartTime: undefined,
          },
        };
        callbacksRef.current.onSyncFailed?.({
          eventData,
          previousJobs: previous.jobs,
          nextJobs: next.jobs,
        });
        return next;
      });
    };

    if (!socketService.isConnected()) {
      socketService.connect();
    }

    socketService.on('sync:jobs_started', handleSyncStarted);
    socketService.on('sync:jobs_completed', handleSyncCompleted);
    socketService.on('sync:jobs_failed', handleSyncFailed);

    return () => {
      clearInterval(pollInterval);
      socketService.off('sync:jobs_started', handleSyncStarted);
      socketService.off('sync:jobs_completed', handleSyncCompleted);
      socketService.off('sync:jobs_failed', handleSyncFailed);
    };
  }, [enabled, pollMs, refreshSyncStatus]);

  return {
    syncStatus,
    refreshSyncStatus,
    setSyncStatus,
  };
}

export default useJobSyncStatus;
